import {
  IconChevronDown,
  IconChevronUp,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";

const PillButton: FC<{
  label: string;
  icon: ReactNode;
  onPress: () => void;
  isDisabled?: boolean;
}> = ({ label, icon, onPress, isDisabled }) => (
  <AriaButton
    aria-label={label}
    onPress={onPress}
    isDisabled={isDisabled}
    className="flex size-5 shrink-0 items-center justify-center rounded text-gray-600 outline-none hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
  >
    {icon}
  </AriaButton>
);

/**
 * Chrome find-in-page style bar for stepping through transcript search matches.
 * The query shows as read-only inline text in a single compact pill; prev/next
 * page the current match and close dismisses the search (clearing highlights).
 */
export const TranscriptSearchBar: FC<{
  query: string;
  count: number;
  currentMatchIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}> = ({ query, count, currentMatchIndex, onPrev, onNext, onClose }) => {
  return (
    <div className="flex h-7 w-72 items-center gap-0.5 rounded-md border border-border-strong bg-white pr-1 pl-2.5 shadow-xs">
      <IconSearch className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      <span className="ml-1.5 min-w-0 flex-1 truncate text-xs text-foreground">
        {query}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {currentMatchIndex + 1}/{count}
      </span>
      <div className="mx-1 h-4 w-px shrink-0 bg-border-light" />
      <PillButton
        label="Previous match"
        icon={<IconChevronUp className="h-3.5 w-3.5" />}
        onPress={onPrev}
        isDisabled={currentMatchIndex === 0}
      />
      <PillButton
        label="Next match"
        icon={<IconChevronDown className="h-3.5 w-3.5" />}
        onPress={onNext}
        isDisabled={currentMatchIndex === count - 1}
      />
      <PillButton
        label="Close search"
        icon={<IconX className="h-3.5 w-3.5" />}
        onPress={onClose}
      />
    </div>
  );
};
